// Presence (PROTOCOL.md §10.12, W9b): where each member's writer is in a shared project. Platform-neutral pieces the app
// (and any other client) puts together:
//
// - the payload, sealed with the project key under `writing-suite/presence/v1:<owner>:<project>` and opened only when
//   its `member`/`device` are the ones the relay says sent it;
// - `PresenceSender`: this device's state, sent on caret moves at most 4 times a second (the last state always goes),
//   again every 15 s as a heartbeat, `typing` for 2 s after an edit, and `gone` when idle for 3 minutes or hidden;
// - `PresenceRoster`: the others' latest frames, each dropped `ttl` after it arrived or at once on `gone`;
// - `resolveSelection`: a payload's positions (scalars at the sender's heads) as UTF-16 offsets in the text now, the way
//   comment anchors resolve — or null while the document doesn't have those heads yet (the text is on its way).
import * as A from '@automerge/automerge';
import { decodeHeadsIn, ROOT, scalarOffsetOfUTF16, SwiftReader, utf16OffsetOfScalar } from '@writing-suite/core';
import { utf8 } from '../shared/b64.ts';
import { open, seal } from '../shared/crypto.ts';
import type { Timers } from './outbox.ts';

export const PRESENCE_TTL_MS = 30_000;
export const HEARTBEAT_MS = 15_000;
/** At most 4 frames a second. */
export const THROTTLE_MS = 250;
export const IDLE_MS = 3 * 60_000;
export const TYPING_MS = 2_000;

export interface PresencePayload {
  v: 1;
  member: string;
  device: string;
  /** Increasing per device: an older frame than one already had is ignored. */
  seq: number;
  /** The name the sender goes by (when the project's members map has none for them). */
  name?: string;
  /** The fragment the caret is in, or null (in the project, in no scene). */
  fragment: string | null;
  /** The selection in Unicode scalars of the fragment's text at `heads` (head = the caret). */
  anchor?: number;
  head?: number;
  heads?: string;
  typing: boolean;
}

/** Where this device's writer is: what `PresenceSender.update` takes. Offsets as the payload carries them. */
export type PresenceState = Pick<PresencePayload, 'fragment' | 'anchor' | 'head' | 'heads'>;

export const presenceAAD = (owner: string, projectId: string) => `writing-suite/presence/v1:${owner}:${projectId}`;

const textDecoder = new TextDecoder();

export async function sealPresence(key: Uint8Array, aad: string, payload: PresencePayload): Promise<Uint8Array> {
  return seal(key, utf8(JSON.stringify(payload)), aad);
}

const isCount = (n: unknown) => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0;
const isID = (s: unknown) => typeof s === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(s);

/**
 * A received payload opened and checked, or null: it must open with the key (and this project's associated data), be
 * version 1, and name the account and device the relay says sent it (one member can't speak for another).
 */
export async function openPresence(key: Uint8Array, aad: string, sealed: Uint8Array, from: { account: string; device: string }): Promise<PresencePayload | null> {
  let p: Partial<PresencePayload>;
  try {
    p = JSON.parse(textDecoder.decode(await open(key, sealed, aad))) as Partial<PresencePayload>;
  } catch {
    return null;
  }
  if (!p || p.v !== 1 || p.member !== from.account || p.device !== from.device || !isCount(p.seq)) return null;
  if (p.fragment !== null && !isID(p.fragment)) return null;
  const positions = p.anchor !== undefined || p.head !== undefined || p.heads !== undefined;
  if (positions && (!isCount(p.anchor) || !isCount(p.head) || typeof p.heads !== 'string' || !/^(?:[0-9a-f]{64})+$/.test(p.heads))) return null;
  return {
    v: 1, member: p.member, device: p.device, seq: p.seq!, fragment: p.fragment ?? null, typing: p.typing === true,
    ...(typeof p.name === 'string' ? { name: p.name.slice(0, 80) } : {}),
    ...(positions ? { anchor: p.anchor, head: p.head, heads: p.heads } : {}),
  };
}

/** UTF-16 offsets in the fragment's text now for a payload's selection, or null when the document doesn't have the
 *  sender's heads yet (or it carries no positions). */
export function resolveSelection(doc: A.Doc<unknown>, p: Pick<PresencePayload, 'anchor' | 'head' | 'heads'>): { anchor: number; head: number } | null {
  if (p.heads === undefined || p.anchor === undefined || p.head === undefined) return null;
  const heads = decodeHeadsIn(p.heads, doc);
  if (!heads) return null;
  const then = new SwiftReader(doc, heads).get(ROOT, 'text');
  const now = new SwiftReader(doc).get(ROOT, 'text');
  if (!then || then[0] !== 'text' || !now || now[0] !== 'text') return null;
  const thenId = then[1] as string, nowId = now[1] as string;
  const r = new SwiftReader(doc, heads);
  const thenText = r.text(thenId);
  const b = A.getBackend(doc);
  const at = (scalar: number) => {
    const u16 = Math.min(thenText.length, utf16OffsetOfScalar(thenText, scalar));
    return b.getCursorPosition(nowId, b.getCursor(thenId, u16, heads));
  };
  try {
    return { anchor: at(p.anchor), head: at(p.head) };
  } catch {
    return null;
  }
}

/** This device's caret as a payload's positions: scalars of the fragment's text now, at its heads. */
export function selectionAt(doc: A.Doc<unknown>, anchorUTF16: number, headUTF16: number): Pick<PresencePayload, 'anchor' | 'head' | 'heads'> | null {
  const r = new SwiftReader(doc);
  const text = r.string(ROOT, 'text');
  if (text === null || text === undefined) return null;
  return {
    anchor: scalarOffsetOfUTF16(text, Math.min(anchorUTF16, text.length)),
    head: scalarOffsetOfUTF16(text, Math.min(headUTF16, text.length)),
    heads: [...A.getHeads(doc)].map((h) => h.toLowerCase()).sort().join(''),
  };
}

const defaultTimers: Timers = { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };

export interface SenderOptions {
  /** Sends this state (with `typing`), or `null` for `gone`. False when it couldn't go (no connection). */
  send(state: (PresenceState & { typing: boolean }) | null): boolean;
  timers?: Timers;
  now?: () => number;
  throttleMs?: number;
  heartbeatMs?: number;
  idleMs?: number;
  typingMs?: number;
}

/**
 * This device's presence on its way out: `update` with every caret move, `typed` with every edit, `setVisible` with
 * the page's visibility, `resend` when the connection is back. Frames go at most every `throttleMs` (the newest state
 * always goes, late by at most that), and every `heartbeatMs` while nothing changes; after `idleMs` without `update`,
 * `typed` or `activity`, or while hidden, `gone` goes once and nothing more until the writer is back.
 */
export class PresenceSender {
  private state: PresenceState = { fragment: null };
  private typing = false;
  private lastSent = -Infinity;
  private trailing: unknown = null;
  private heartbeat: unknown = null;
  private idle: unknown = null;
  private typingTimer: unknown = null;
  private visible = true;
  private idled = false;
  /** `gone` was sent (or nothing has been yet): the next state goes at once. */
  private away = true;
  private stopped = false;
  private readonly timers: Timers;
  private readonly now: () => number;

  constructor(private readonly o: SenderOptions) {
    this.timers = o.timers ?? defaultTimers;
    this.now = o.now ?? Date.now;
  }

  get present(): boolean {
    return !this.away;
  }

  /** The caret moved (or the scene changed). */
  update(state: PresenceState) {
    this.state = { ...state };
    this.activity();
  }

  /** The writer typed: `typing` for the next `typingMs`. */
  typed() {
    const was = this.typing;
    this.typing = true;
    if (this.typingTimer !== null) this.timers.clear(this.typingTimer);
    this.typingTimer = this.timers.set(() => {
      this.typingTimer = null;
      this.typing = false;
      this.schedule();
    }, this.o.typingMs ?? TYPING_MS);
    if (!was) this.activity();
    else this.touch();
  }

  /** The writer did something (keys, pointer): no longer idle. Sends when there's something new to say. */
  activity() {
    this.touch();
    this.schedule();
  }

  /** The page was hidden (gone) or shown again (back). */
  setVisible(visible: boolean) {
    if (this.visible === visible) return;
    this.visible = visible;
    if (!visible) this.leave();
    else this.activity();
  }

  /** The connection is back: what's current goes now. */
  resend() {
    if (this.stopped || !this.visible || this.idled) return;
    this.lastSent = -Infinity;
    this.flush();
  }

  /** Closing: `gone`, and nothing more. */
  stop() {
    if (!this.away) this.o.send(null);
    this.away = true;
    this.stopped = true;
    for (const t of [this.trailing, this.heartbeat, this.idle, this.typingTimer]) if (t !== null) this.timers.clear(t);
    this.trailing = this.heartbeat = this.idle = this.typingTimer = null;
  }

  private touch() {
    if (this.stopped) return;
    if (this.idle !== null) this.timers.clear(this.idle);
    this.idle = this.timers.set(() => {
      this.idle = null;
      this.idled = true;
      this.leave();
    }, this.o.idleMs ?? IDLE_MS);
    this.idled = false;
  }

  private leave() {
    if (this.trailing !== null) this.timers.clear(this.trailing);
    if (this.heartbeat !== null) this.timers.clear(this.heartbeat);
    this.trailing = this.heartbeat = null;
    if (!this.away) this.o.send(null);
    this.away = true;
  }

  /** The state goes now, or when the throttle allows. */
  private schedule() {
    if (this.stopped || !this.visible || this.idled) return;
    const wait = this.lastSent + (this.o.throttleMs ?? THROTTLE_MS) - this.now();
    if (wait <= 0 || this.away) return this.flush();
    if (this.trailing === null) {
      this.trailing = this.timers.set(() => {
        this.trailing = null;
        this.flush();
      }, wait);
    }
  }

  private flush() {
    if (this.trailing !== null) this.timers.clear(this.trailing);
    this.trailing = null;
    if (this.stopped || !this.visible || this.idled) return;
    const sent = this.o.send({ ...this.state, typing: this.typing });
    if (!sent) return; // offline: `resend` when the connection is back
    this.away = false;
    this.lastSent = this.now();
    if (this.heartbeat !== null) this.timers.clear(this.heartbeat);
    this.heartbeat = this.timers.set(() => {
      this.heartbeat = null;
      this.lastSent = -Infinity;
      this.flush();
    }, this.o.heartbeatMs ?? HEARTBEAT_MS);
  }
}

export interface PresenceEntry {
  account: string;
  device: string;
  payload: PresencePayload;
  /** When it was received, and when it lapses without a newer frame. */
  at: number;
  expiresAt: number;
}

/**
 * The others' presence: the newest frame per device, until its TTL lapses or the device says `gone`. Frames from this
 * account (another tab or device of the same writer) are kept out: presence is about other people.
 */
export class PresenceRoster {
  private byDevice = new Map<string, PresenceEntry>();
  private listeners = new Set<() => void>();
  private expiry: unknown = null;
  private readonly timers: Timers;
  private readonly now: () => number;
  /** A snapshot that changes identity only when the roster does (for `useSyncExternalStore`). */
  private snapshot: PresenceEntry[] = [];

  constructor(private readonly o: { self: string; timers?: Timers; now?: () => number }) {
    this.timers = o.timers ?? defaultTimers;
    this.now = o.now ?? Date.now;
  }

  /** A frame received (already opened and checked). False when it was ignored (this account's, or older). */
  put(payload: PresencePayload, ttl: number): boolean {
    if (payload.member === this.o.self) return false;
    const had = this.byDevice.get(payload.device);
    if (had && had.payload.member === payload.member && had.payload.seq >= payload.seq && had.expiresAt > this.now()) return false;
    const at = this.now();
    this.byDevice.set(payload.device, { account: payload.member, device: payload.device, payload, at, expiresAt: at + ttl });
    this.changed();
    return true;
  }

  /** A device left. */
  gone(from: { account: string; device: string }) {
    const had = this.byDevice.get(from.device);
    if (!had || had.account !== from.account) return;
    this.byDevice.delete(from.device);
    this.changed();
  }

  /** Everyone at once (the connection closed: nothing is known until it's back). */
  clear() {
    if (!this.byDevice.size) return;
    this.byDevice.clear();
    this.changed();
  }

  entries(): PresenceEntry[] {
    return this.snapshot;
  }

  /** The others whose caret is in `fragmentId`, one per account (its newest device). */
  inFragment(fragmentId: string): PresenceEntry[] {
    return this.perAccount(this.snapshot.filter((e) => e.payload.fragment === fragmentId));
  }

  /** One entry per account (the most recent), in the order they arrived. */
  perAccount(entries = this.snapshot): PresenceEntry[] {
    const best = new Map<string, PresenceEntry>();
    for (const e of entries) {
      const b = best.get(e.account);
      if (!b || e.at > b.at) best.set(e.account, e);
    }
    return [...best.values()].sort((a, b) => a.account.localeCompare(b.account));
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Drops what lapsed; `stop` ends the timer. */
  expire() {
    const now = this.now();
    let any = false;
    for (const [device, e] of this.byDevice) {
      if (e.expiresAt <= now) {
        this.byDevice.delete(device);
        any = true;
      }
    }
    if (any) this.changed();
    else this.arm();
  }

  stop() {
    if (this.expiry !== null) this.timers.clear(this.expiry);
    this.expiry = null;
    this.listeners.clear();
  }

  private changed() {
    this.snapshot = [...this.byDevice.values()].sort((a, b) => a.at - b.at);
    this.arm();
    for (const fn of [...this.listeners]) fn();
  }

  private arm() {
    if (this.expiry !== null) this.timers.clear(this.expiry);
    this.expiry = null;
    if (!this.byDevice.size) return;
    const next = Math.min(...[...this.byDevice.values()].map((e) => e.expiresAt));
    this.expiry = this.timers.set(() => {
      this.expiry = null;
      this.expire();
    }, Math.max(0, next - this.now()) + 5);
  }
}
